import React, {useState} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router';

import {UserAvatar} from 'app/components/general/avatars';
import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {CurrentUserSelector} from 'app/current-user/api';
import {
  createFormErrorsContext,
  FormControlGroup,
  FormErrorsProps,
} from 'app/general/forms';
import {showNotification} from 'app/toasts/slice';
import {OWNERSHIP_TRANSFER_API, USERS_HOME} from 'app/urls';
import {
  selectCommonFixedSelectProps,
  SelectUsersSingle,
} from 'app/user/components/select';
import {gt} from 'app/utils/i18n';
import {createErrorHandler, postJson} from 'app/utils/request';

import {UserSerializedType} from 'sweet-types';

import type {ExtraPropsType} from 'app/components/general/SweetReactSelect';

interface NoOptionsMessageProps {
  selectProps: {
    extraProps: ExtraPropsType<UserSerializedType>;
  };
}

function NoOptionsMessage({
  selectProps: {
    extraProps: {inputText = ''},
  },
}: NoOptionsMessageProps) {
  const searchText = <span className="font-weight-bold">{inputText}</span>;
  return (
    <div className="text-secondary p-2">
      {inputText === ''
        ? gt('No Super Managers with verified email address were found.')
        : gt(
            'No Super Managers with verified email address were found searching for %s.',
            searchText
          )}
      <Link className="btn btn-sm btn-link" to={USERS_HOME}>
        {gt('Manage Members')}
      </Link>
    </div>
  );
}

type OwnProps = {
  isOwner: boolean;
  isSamlOnly: boolean;
};
type Props = OwnProps & FormErrorsProps;
export function BaseTransferOwnershipForm({
  isOwner,
  isSamlOnly,
  captureFormErrorsFromRejection,
}: Props): JSX.Element {
  const [newOwner, setNewOwner] = useState<UserSerializedType | null>(null);
  const [showingModal, showModal] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'transfering' | 'success'>(
    'idle'
  );
  const userId = useSelector((state) => CurrentUserSelector(state).data!.id);
  const dispatch = useDispatch();

  function submit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    if (newOwner != null) {
      setStatus('transfering');
      postJson(OWNERSHIP_TRANSFER_API, {
        new_owner: newOwner.url,
        password,
      })
        .then(
          () => setStatus('success'),
          (err) => {
            setStatus('idle');
            captureFormErrorsFromRejection(err);
            throw err;
          }
        )
        .catch(
          createErrorHandler(
            gt('Unable to transfer the account ownership.'),
            dispatch,
            {
              '403': (error) =>
                dispatch(showNotification(error.data.detail, 'error')),
            }
          )
        );
    }
  }

  function closeModal() {
    showModal(false);

    if (status === 'success') {
      /* if the transfer was successful we need to reload the page
            to hide the owner specific fields rendered by django */
      window.location.reload();
    }
  }

  return (
    <>
      {isOwner ? (
        <>
          <h4 className="section pb-2 border-bottom">
            {gt('Transfer Account Ownership')}
          </h4>
          <p>
            {gt(
              'Transfers ownership of your company SweetProcess account to a super manager on your account. They will take your place as account owner, and you will become a super manager.  If you then wish to leave the SweetProcess account, the newly promoted owner may remove you.'
            )}
          </p>
          <div className="form-group row">
            <div className="col-form-label col-xl-2 col-lg-3 col-md-3 col-sm-4"></div>
            <div className="col-xl-3 col-lg-5 col-md-5 col-sm-6">
              {isSamlOnly ? (
                <div className="alert alert-danger">
                  {gt(
                    'Your account has Single Sign On enabled. Please contact support@sweetprocess.com to transfer ownership.'
                  )}
                </div>
              ) : null}
              <button
                disabled={isSamlOnly}
                onClick={() => showModal(true)}
                className="btn btn-danger"
              >
                {gt('Transfer Account Ownership')}
              </button>
            </div>
          </div>
        </>
      ) : null}
      <TabbedModal
        show={showingModal}
        onCancel={closeModal}
        tabs={[
          {
            eventKey: 'main-tab',
            name: gt('Transfer Account Ownership'),
            icon: 'fa fa-coffee',
            content:
              status === 'idle' || status === 'transfering' ? (
                <form onSubmit={submit}>
                  <p>
                    {gt(
                      "You can only transfer Account Ownership to a Super Manager with a verified email address. This action is permanent and can't be undone. You will continue to be an account member as Super Manager."
                    )}
                  </p>
                  <FormControlGroup field="new_owner" className="mt-4">
                    <label htmlFor="exampleFormControlInput1">
                      {gt('New Account Owner')}
                    </label>
                    <SelectUsersSingle
                      params={{
                        account_role: 'super_manager',
                        exclude_id: userId,
                        status: 'email_verified',
                      }}
                      placeholder={gt('Select a Super Manager...')}
                      selected={newOwner}
                      onChange={(value) => {
                        setNewOwner(value);
                        return Promise.resolve(true);
                      }}
                      components={{
                        ...selectCommonFixedSelectProps.components,
                        NoOptionsMessage,
                      }}
                    />
                  </FormControlGroup>
                  <FormControlGroup field="password" className="mt-4">
                    <label htmlFor="transfer-password-field">
                      {gt('Enter your password to confirm this transfer.')}
                    </label>
                    <input
                      type="password"
                      value={password}
                      className="form-control"
                      id="transfer-password-field"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </FormControlGroup>
                  <StyledModalFooter>
                    <button
                      type="submit"
                      onClick={() => showModal(true)}
                      className="btn btn-danger"
                      disabled={
                        password === '' ||
                        newOwner == null ||
                        status === 'transfering'
                      }
                    >
                      {status === 'transfering' ? (
                        <>
                          <i className="fa fa-circle-notch fa-spin" />{' '}
                          {gt('Transfering...')}
                        </>
                      ) : (
                        gt('Transfer Account Ownership')
                      )}
                    </button>
                  </StyledModalFooter>
                </form>
              ) : status === 'success' ? (
                <>
                  <div className="text-center">
                    <p className="lead">
                      <i className="fa fa-check-circle text-success mr-2" />
                      {gt('Ownership Transfer Successful')}
                    </p>
                    {newOwner != null ? (
                      <div className="card">
                        <div className="card-body">
                          <p>
                            {gt(
                              'The account ownership has been transferred to:'
                            )}
                          </p>
                          <div className="d-flex justify-content-center align-items-center mx-5">
                            <UserAvatar user={newOwner} />
                            <div className="ml-2 text-left small">
                              <div>{newOwner.name}</div>
                              <div className="text-secondary font-italic">
                                {newOwner.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <p className="mt-4">
                      {gt('You are no longer the owner of this account.')}
                    </p>
                    <p className="small">
                      {gt('We have notified the new owner about the transfer.')}
                    </p>
                  </div>
                  <StyledModalFooter>
                    <button onClick={closeModal} className="btn btn-primary">
                      {gt('Close')}
                    </button>
                  </StyledModalFooter>
                </>
              ) : null,
          },
        ]}
      />
    </>
  );
}

export const TransferOwnershipForm = createFormErrorsContext<Props>(
  BaseTransferOwnershipForm
);
